import { useEffect, useRef, useState } from 'react';
import type { Ingredient, InventoryUnit } from '../../types/inventory';
import { ProgressiveSection } from '../ui/ProgressiveSection';
import { AddLink, Answers, Question } from './index';

/** Lo único que el selector usa: así también lo reutiliza Mermas, cuya lista no trae costos ni existencias. */
export type PickableIngredient = Pick<Ingredient, 'id' | 'name' | 'isActive' | 'approvalStatus'> & { baseUnit: Pick<InventoryUnit, 'name' | 'symbol'> };

// Selector de insumo con buscador y lista (el mismo de Capturar ticket), como pieza compartida.
// Nada preseleccionado: sin insumo elegido muestra la lista; con insumo, una fila encogida con
// «Cambiar». Sin autofoco en el buscador: muchas veces se elige de la lista sin escribir.
// P-079: «Cambiar» reabre la lista con el insumo actual marcado y NO lo vacía (Codex R1 · K11): solo
// elegir otro lo sustituye; elegir el mismo deja todo como estaba. Al reabrir, el foco pasa a la lista
// (su contenedor, no el buscador: no se levanta el teclado), porque el botón «Cambiar» desaparece (Codex R2 · R1).

const MAX_MATCHES = 40;
/** Búsqueda sin acentos ni mayúsculas: «azucar» encuentra «Azúcar». */
function normalize(text: string) { return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase(); }

export function IngredientPicker({ ingredients, value, onChange, onCreateNew, createLabel, question = '¿Cuál insumo?' }: {
  ingredients: PickableIngredient[];
  value: string;
  onChange: (ingredientId: string) => void;
  onCreateNew?: () => void;
  createLabel?: string;
  question?: string;
}) {
  const [search, setSearch] = useState('');
  const [picking, setPicking] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!picking) return;
    const active = document.activeElement;
    if (!active || active === document.body) listRef.current?.focus({ preventScroll: true });
  }, [picking]);
  const chosen = value ? ingredients.find((ingredient) => ingredient.id === value) : undefined;
  if (chosen && !picking) {
    return <ProgressiveSection complete label="Insumo" onOpen={() => setPicking(true)} summary={`${chosen.name} · se mide en ${chosen.baseUnit.symbol}`} />;
  }
  const needle = normalize(search.trim());
  const matches = ingredients.filter((ingredient) => ingredient.isActive && ingredient.approvalStatus !== 'REJECTED' && (!needle || normalize(ingredient.name).includes(needle)));
  const pick = (ingredientId: string) => { setPicking(false); if (ingredientId !== value) onChange(ingredientId); };
  return <>
    <Question>{question}</Question>
    <Answers>
      <div aria-label={question} className="wiz-picker" ref={listRef} role="group" tabIndex={-1}>
        <div className="field wiz-field"><input aria-label="Buscar insumo" enterKeyHint="search" onChange={(event) => setSearch(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); }} placeholder="Buscar por nombre…" value={search} /></div>
        <div className="wiz-list">
          {matches.slice(0, MAX_MATCHES).map((ingredient) => (
            <button aria-pressed={ingredient.id === value} className={`wiz-choice${ingredient.id === value ? ' wiz-choice--active' : ''}`} key={ingredient.id} onClick={() => pick(ingredient.id)} type="button">
              <strong>{ingredient.name}</strong>
              <small>Se mide en {ingredient.baseUnit.name} ({ingredient.baseUnit.symbol}){ingredient.approvalStatus === 'PENDING_APPROVAL' ? ' · pendiente de aprobación' : ''}</small>
            </button>
          ))}
        </div>
      </div>
      {matches.length > MAX_MATCHES && <p className="wiz-hint">Hay más insumos; afina la búsqueda.</p>}
      {matches.length === 0 && <p className="wiz-hint">{ingredients.length === 0 ? 'Cargando insumos…' : 'No hay insumos con ese nombre.'}</p>}
    </Answers>
    {onCreateNew && <AddLink onClick={onCreateNew}>{createLabel ?? '+ Agregar un insumo que no está en la lista'}</AddLink>}
  </>;
}
